import { ExtendedObservation } from '@seen/core/models/observation'
import {
  OBSERVATION_NODE,
  OBSERVATION_PANEL_NODE,
} from '@seen/core/observation-logic/documentUnits'
import { observationReferenceStrings } from '@seen/core/observationReferences'
import { ObservationNode as ObservationSchema } from '@seen/core/tiptap/schema'
import type { Editor } from '@tiptap/core'
import { Fragment, type Node as PMNode, Slice } from '@tiptap/pm/model'
import {
  NodeSelection,
  Plugin,
  PluginKey,
  Selection,
  type Transaction,
} from '@tiptap/pm/state'
import type { EditorView } from '@tiptap/pm/view'
import { ReactNodeViewRenderer } from '@tiptap/react'
import {
  type InputRemovedRecords,
  observationInputRemovalPlugin,
} from '#src/components/rich-text-editor/ObservationNode/observationInputRemoval.ts'
import { ObservationNodeView } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeView.tsx'
import type {
  ObservationNodeOptions,
  RecordedObservationNode,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { resilientNodeView } from '#src/components/rich-text-editor/ResilientNodeView.tsx'

interface ObsNodeOptions extends ObservationNodeOptions {
  onObservationsChanged?: (payload: {
    added: string[]
    removed: string[]
  }) => void
  /* Set only with structured capture on: removes the records of nodes and
   * panels typed or pasted over, which skip the Backspace/Delete
   * confirmation. */
  onRemoveRecords?: (removed: InputRemovedRecords) => void
}

export const ObservationNode = ObservationSchema.extend<ObsNodeOptions>({
  addKeyboardShortcuts() {
    const guard = (direction: 'backward' | 'forward') => () =>
      guardRecordedRemoval(
        this.editor,
        this.name,
        direction,
        this.options.onConfirmRemove
      )
    return {
      Backspace: guard('backward'),
      'Mod-Backspace': guard('backward'),
      'Shift-Backspace': guard('backward'),
      Delete: guard('forward'),
      'Mod-Delete': guard('forward'),
    }
  },

  addProseMirrorPlugins() {
    const typeName = this.name
    let lastCutReferenceStrings: Set<string> = new Set()
    let lastCutPanelIds: Set<string> = new Set()
    const { onObservationsChanged, onRemoveRecords } = this.options
    return [
      ...(onRemoveRecords
        ? [observationInputRemovalPlugin({ typeName, onRemoveRecords })]
        : []),
      new Plugin({
        key: OBSERVATION_PLUGIN_KEY,
        props: {
          // Track which reference strings were CUT
          handleDOMEvents: {
            cut(view) {
              const { state } = view
              const { from, to } = state.selection
              lastCutReferenceStrings =
                collectObservationReferenceStringsInSelection(
                  state.doc,
                  from,
                  to,
                  typeName
                )
              lastCutPanelIds = collectPanelIdsInSelection(state.doc, from, to)
              // let browser/prosemirror continue with the actual cut
              return false
            },
          },
          transformPasted: (slice: Slice, view: EditorView) => {
            const cutReferenceStrings = lastCutReferenceStrings
            const cutPanelIds = lastCutPanelIds
            lastCutReferenceStrings = new Set()
            lastCutPanelIds = new Set()
            // A drag within this editor moves what it drops.
            const dragMove = Boolean(view.dragging?.move)

            return mapSlice(slice, (node) => {
              if (node.type.name === OBSERVATION_PANEL_NODE) {
                const moved =
                  dragMove || cutPanelIds.has(node.attrs.id as string)
                return moved ? node : copiedPanel(node)
              }
              const references = observationReferenceStrings(node.attrs)
              const held = node.attrs.draft || node.attrs.drafts?.length
              // A copy starts unanswered. A cut keeps the records, or the
              // answers held in the note, it moved.
              const moved =
                dragMove ||
                (references.length > 0
                  ? references.every((reference) =>
                      cutReferenceStrings.has(reference)
                    )
                  : cutPanelIds.has(node.attrs.id as string))
              if (
                node.type.name === typeName &&
                (references.length || held) &&
                !moved
              ) {
                return node.type.create(
                  {
                    ...node.attrs,
                    reference: null,
                    references: null,
                    groupId: null,
                    draft: null,
                    drafts: null,
                  },
                  node.content,
                  node.marks
                )
              }
              return node
            })
          },
        },
        appendTransaction(transactions, oldState, newState) {
          // No doc change → nothing to do
          if (!transactions.some((tr) => tr.docChanged)) {
            return null
          }

          // TODO: multiplayer support via tagging local transactions via tr.setMeta

          const before = collectObservationReferenceStrings(
            oldState.doc,
            typeName
          )
          const beforeSet = new Set(before)
          const after = collectObservationReferenceStrings(
            newState.doc,
            typeName
          )
          const afterSet = new Set(after)

          const removed = before.filter((id) => !afterSet.has(id))
          const added = after.filter((id) => !beforeSet.has(id))

          if ((added.length || removed.length) && onObservationsChanged) {
            onObservationsChanged({ added, removed })
          }

          // No additional transaction to append
          return null
        },
      }),
    ]
  },

  /* The one attribute the shared schema deliberately does not carry: transient
   * per-client focus state, `rendered: false`, so it never reaches stored
   * content and the server has nothing to parse back. Spreads the parent's
   * attributes rather than replacing them — dropping that spread would fork
   * this node's schema from the server's. */
  addAttributes() {
    return {
      ...this.parent?.(),
      autofocus: {
        default: false,
        rendered: false,
      },
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(
      resilientNodeView(ObservationNodeView, { inline: true }),
      {
        className: 'group',
      }
    )
  },
})

const OBSERVATION_PLUGIN_KEY = new PluginKey<{
  lastCutReferenceStrings: Set<string>
}>('observationPlugin')

function mapFragment(
  fragment: Fragment,
  f: (node: PMNode) => PMNode
): Fragment {
  const children: PMNode[] = []
  for (let i = 0; i < fragment.childCount; i++) {
    const child = fragment.child(i)
    let mapped = child
    if (child.content.size) {
      mapped = child.copy(mapFragment(child.content, f))
    }
    children.push(f(mapped))
  }
  return Fragment.fromArray(children)
}

function mapSlice(slice: Slice, f: (node: PMNode) => PMNode): Slice {
  return new Slice(
    mapFragment(slice.content, f),
    slice.openStart,
    slice.openEnd
  )
}

// A pasted copy of a panel is a new, unanswered capture of the same
// instrument: left with the original's group and panel record, it would read
// and write that capture, a signed visit's included.
function copiedPanel(node: PMNode): PMNode {
  return node.type.create(
    {
      ...node.attrs,
      id: crypto.randomUUID(),
      groupId: null,
      panelReference: null,
      frozenReference: null,
      summary: null,
      answers: null,
    },
    node.content,
    node.marks
  )
}

// The panels and observation nodes a cut takes, by id: a cut moves what
// they hold, answers held in the note included.
function collectPanelIdsInSelection(
  doc: PMNode,
  from: number,
  to: number
): Set<string> {
  const ids = new Set<string>()
  doc.nodesBetween(from, to, (node) => {
    if (
      (node.type.name === OBSERVATION_PANEL_NODE ||
        node.type.name === OBSERVATION_NODE) &&
      node.attrs.id
    ) {
      ids.add(node.attrs.id as string)
    }
    return true
  })
  return ids
}

function collectObservationReferenceStringsInSelection(
  doc: PMNode,
  from: number,
  to: number,
  nodeTypeName: string
): Set<string> {
  const referenceStrings = new Set<string>()
  doc.nodesBetween(from, to, (node) => {
    if (node.type.name === nodeTypeName) {
      for (const reference of observationReferenceStrings(node.attrs)) {
        referenceStrings.add(reference)
      }
    }
    return true
  })
  return referenceStrings
}

function collectObservationReferenceStrings(doc: PMNode, typeName: string) {
  const referenceStrings: string[] = []
  doc.descendants((node) => {
    if (node.type.name === typeName) {
      referenceStrings.push(...observationReferenceStrings(node.attrs))
    }
    return true
  })
  return referenceStrings
}

/* A recorded node is never removed by a single keystroke: the first press
 * next to it selects it, and deleting a selection that holds one waits for
 * `onConfirmRemove`. Cut is a move and goes through the cut handler instead. */
function guardRecordedRemoval(
  editor: Editor,
  typeName: string,
  direction: 'backward' | 'forward',
  onConfirmRemove: ObservationNodeOptions['onConfirmRemove']
): boolean {
  if (!onConfirmRemove || !editor.isEditable) {
    return false
  }
  const { state, view } = editor
  const { selection } = state

  if (selection.empty) {
    const { $from } = selection
    const adjacent =
      direction === 'backward' ? $from.nodeBefore : $from.nodeAfter
    if (!adjacent || !isRecorded(adjacent, typeName)) {
      return false
    }
    const pos =
      direction === 'backward' ? $from.pos - adjacent.nodeSize : $from.pos
    view.dispatch(state.tr.setSelection(NodeSelection.create(state.doc, pos)))
    return true
  }

  const records = recordedNodesBetween(
    state.doc,
    selection.from,
    selection.to,
    typeName
  )
  if (!records.length) {
    return false
  }

  const doc = state.doc
  const selectionJSON = selection.toJSON()
  void onConfirmRemove(records).then((confirmed) => {
    if (!confirmed || editor.isDestroyed) {
      return
    }
    const current = editor.state
    const tr = current.doc.eq(doc)
      ? current.tr
          .setSelection(Selection.fromJSON(current.doc, selectionJSON))
          .deleteSelection()
      : deleteRecordedNodes(
          current.tr,
          typeName,
          new Set(records.map((r) => r.reference))
        )
    editor.view.dispatch(tr.scrollIntoView())
  })
  return true
}

function isRecorded(node: PMNode, typeName: string): boolean {
  return (
    node.type.name === typeName &&
    observationReferenceStrings(node.attrs).length > 0
  )
}

function recordedNodesBetween(
  doc: PMNode,
  from: number,
  to: number,
  typeName: string
): RecordedObservationNode[] {
  const records: RecordedObservationNode[] = []
  doc.nodesBetween(from, to, (node) => {
    if (isRecorded(node, typeName)) {
      const label = ExtendedObservation.getNickname(
        node.attrs.code,
        node.attrs.codingDisplay || node.attrs.display
      )
      for (const reference of observationReferenceStrings(node.attrs)) {
        records.push({ reference, label })
      }
    }
    return true
  })
  return records
}

// The document moved on while the confirmation was open, so the old selection
// no longer lines up; remove just the confirmed nodes wherever they now sit.
function deleteRecordedNodes(
  tr: Transaction,
  typeName: string,
  references: Set<string>
): Transaction {
  const positions: { pos: number; size: number }[] = []
  tr.doc.descendants((node, pos) => {
    if (
      node.type.name === typeName &&
      observationReferenceStrings(node.attrs).some((reference) =>
        references.has(reference)
      )
    ) {
      positions.push({ pos, size: node.nodeSize })
    }
    return true
  })
  for (const { pos, size } of positions.reverse()) {
    tr.delete(pos, pos + size)
  }
  return tr
}
